/** @import {FormMessageProps} from '@radix-ui/react-form' */
/** @import {ComponentPropsWithoutRef, ComponentProps} from 'react' */

import * as FormPrimitive from '@radix-ui/react-form'
import { Cross2Icon, InfoCircledIcon } from '@radix-ui/react-icons'
import { Box, Callout, Flex, IconButton, Slot, Text } from '@radix-ui/themes'
import clsx from 'clsx'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { composeEventHandlers, mergeProps } from '../component-utils.jsx'
import { createContext } from '../context/context-utils.jsx'
import { SemanticColors } from '../styles/colors.js'
import { Button } from './button.jsx'
import * as Disclosure from './disclosure.jsx'

import styles from './form.module.css'

export {
  Root,
  Submit,
  FieldSet,
  FieldSetLegend,
  Field,
  Label,
  Control,
  Description,
  ValidityState,
  ValidationMessage,
  MessageSeverity,
}

const [FormStateContext, useFormState] = createContext('FormState', {
  defaultValue: {
    isSubmitting: false,
    formMessage: null,
  },
})

const [FieldPropsContext, useFieldProps] = createContext('FieldProps', {
  defaultValue: {
    hidden: false,
    required: false,
  },
})

const [FieldValidityContext] = createContext('FieldValidity', {
  defaultValue: {
    validity: /** @type {ValidityState} */ (undefined),
  },
})

const [FieldStateContext, useFieldState] = createContext('FormFieldDataState', {
  defaultValue: {
    'data-valid': /** @type {true | false | undefined} */ (undefined),
    'data-invalid': /** @type {true | false | undefined} */ (undefined),
    'data-required': false,
  },
})

const MessageSeverity = /** @type {const} */ ({
  INFO: 'INFO',
  ERROR: 'ERROR',
  DANGER: 'DANGER',
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
  IGNORED: 'IGNORED',
})

const Root = forwardRef(
  /** @typedef {(formData: FormData) => void | Promise<void>} FormAction */

  /**
   * @typedef {{
   *   message: string
   *   severity?: (typeof MessageSeverity)[keyof MessageSeverity]
   *   fields?: Record<string, any>
   * }} FormActionError
   */

  /**
   * @param {{
   *   action?: FormAction
   *   onActionError?: (error: FormActionError) => FormActionError | null | void
   *   initialMessage?: string | FormActionError
   *   defaultSeverity?: (typeof MessageSeverity)[keyof MessageSeverity]
   * } & Omit<ComponentPropsWithoutRef<typeof FormPrimitive.Root>, 'action'>} props
   */
  function Form(
    {
      action,
      onActionError,
      children,
      initialMessage,
      defaultSeverity = MessageSeverity.ERROR,
      onSubmit,
      onSubmittingChange,
      ...props
    },
    ref,
  ) {
    const $ref = useRef(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Lets a Dialog around the form hold itself open while a submit runs.
    useEffect(() => {
      onSubmittingChange?.(isSubmitting)
    }, [isSubmitting, onSubmittingChange])
    const [formMessage, setFormMessage] = useState(
      /** @type {FormActionError | void} */
      (
        typeof initialMessage === 'string'
          ? {
              severity: defaultSeverity,
              message: initialMessage,
              fields: {},
            }
          : initialMessage && {
              severity: initialMessage.severity ?? defaultSeverity,
              message: initialMessage.message,
              fields: initialMessage.fields ?? {},
            }
      ),
    )

    const [showMessage, setShowMessage] = useState(
      !isSubmitting && !!formMessage,
    )

    useEffect(() => {
      setShowMessage(!isSubmitting && !!formMessage)
    }, [isSubmitting, formMessage])

    const handleSubmit = useCallback(
      /**
       * @param {import('react').SyntheticEvent<
       *   HTMLFormElement,
       *   SubmitEvent
       * >} event
       */
      async (event) => {
        if (typeof action !== 'function') {
          return onSubmit?.(event)
        }

        event.preventDefault()
        setIsSubmitting(true)

        const {
          currentTarget,
          nativeEvent: { submitter },
        } = event

        const formData = new FormData(currentTarget, submitter)

        try {
          await action?.(formData)
          setFormMessage(null)
        } catch (error) {
          const message = error.message ?? error
          const severity = error.severity ?? defaultSeverity

          const fields = [
            error,
            ...(Array.isArray(error.cause) ? error.cause : []),
          ].reduce((fields, cause) => {
            const { parameter } = cause.context ?? {}
            if (parameter?.id && parameter?.location === 'body') {
              fields[parameter.id] ??= []
              fields[parameter.id].push(cause)
            }

            return fields
          }, {})

          const formMessage = {
            message,
            severity,
            fields,
            cause: error,
          }

          const fieldNames = Object.keys(fields)

          /** @type {HTMLElement} */
          const invalidRoot =
            fieldNames.length > 0 &&
            $ref.current?.querySelector(
              fieldNames.map((name) => `[name='${name}']`).join(', '),
            )

          if (invalidRoot) {
            const invalidInput = invalidRoot?.matches('input')
              ? invalidRoot
              : invalidRoot?.querySelector('input')

            invalidInput?.focus({
              preventScroll: false,
            })
          }

          const custom = onActionError?.(formMessage)
          if (!custom || custom.severity !== MessageSeverity.IGNORED) {
            setFormMessage(custom ?? formMessage)
          }
        }
        setIsSubmitting(false)
      },
      [action, onSubmit, defaultSeverity, onActionError],
    )

    return (
      <FormStateContext.Provider value={{ isSubmitting, formMessage }}>
        <FormPrimitive.Root
          {...mergeProps(props, {
            className: styles.root,
          })}
          onInvalid={composeEventHandlers(props.onInvalid, () =>
            setFormMessage(null),
          )}
          onSubmit={handleSubmit}
          ref={ref}
        >
          <StickyBox ref={$ref}>
            <Disclosure.Root
              className={styles.formMessageDisclosureRoot}
              open={showMessage}
            >
              <Disclosure.Content forceMount>
                {!!formMessage && (
                  <FormMessage
                    message={formMessage.message}
                    onClose={() => setShowMessage(false)}
                    severity={formMessage.severity}
                  />
                )}
              </Disclosure.Content>
            </Disclosure.Root>
          </StickyBox>
          <Flex direction="column" flexGrow="1" gap="4" ref={$ref}>
            {children}
          </Flex>
        </FormPrimitive.Root>
      </FormStateContext.Provider>
    )
  },
)

const StickyBox = forwardRef(
  /** @param {import('react').PropsWithChildren} props */
  function StickyBox({ children }, ref) {
    const [isSticky, setIsSticky] = useState(false)
    const boxRef = useRef()

    useEffect(() => {
      const observer =
        ref?.current &&
        new IntersectionObserver(
          ([{ intersectionRatio }]) => {
            setIsSticky(intersectionRatio === 1)
          },
          {
            root: ref?.current,
            rootMargin: '-1px 0px 0px 0px',
            threshold: [1],
          },
        )

      observer?.observe(boxRef?.current)

      return () => observer?.disconnect()
    }, [boxRef, ref, setIsSticky])

    return (
      <Box
        className={clsx(styles.stickyBox, isSticky && styles.stickyBoxFloating)}
        ref={boxRef}
      >
        {children}
      </Box>
    )
  },
)

/**
 * @param {{
 *   onClose: () => void
 *   message: string
 *   severity: keyof typeof MessageSeverity
 * }} props
 */
function FormMessage({
  onClose,
  message = 'Unexpected error',
  severity = MessageSeverity.ERROR,
}) {
  return (
    <Callout.Root
      role="alert"
      className={styles.formMessage}
      color={SemanticColors[severity] ?? SemanticColors[MessageSeverity.ERROR]}
    >
      <Callout.Icon className={styles.formMessageIcon}>
        <InfoCircledIcon />
      </Callout.Icon>
      <Callout.Text className={styles.formMessageText}>{message}</Callout.Text>
      {!!onClose && (
        <Callout.Text>
          <IconButton
            className={styles.formMessageDismissButton}
            onClick={() => onClose()}
            title="Dismiss"
            type="button"
            variant="ghost"
          >
            <Cross2Icon />
          </IconButton>
        </Callout.Text>
      )}
    </Callout.Root>
  )
}

const FieldSet = forwardRef(
  /**
   * @param {{
   *   children: import('react').ReactNode
   * } & ComponentPropsWithoutRef<'fieldset'>} props
   */
  function FieldSet({ name, children, ...props }, ref) {
    const { formMessage } = useFormState()
    const problems = formMessage?.fields[name]

    const isValid = !problems
    const isInvalid = !!problems

    const fieldState = {
      'data-valid': isValid,
      'data-invalid': isInvalid,
    }

    return (
      <fieldset
        ref={ref}
        {...fieldState}
        {...mergeProps(props, {
          name,
          className: styles.fieldset,
        })}
      >
        {children}
        <ProblemDetails as={Text}>{problems}</ProblemDetails>
      </fieldset>
    )
  },
)

const FieldSetLegend = forwardRef(
  /**
   * @param {{
   *   children: import('react').ReactNode
   * } & ComponentPropsWithoutRef<'legend'>} props
   */
  function FieldSetLegend({ children, ...props }, ref) {
    return (
      <legend
        ref={ref}
        {...mergeProps(props, {
          className: [styles.legend, styles.label],
        })}
      >
        {children}
      </legend>
    )
  },
)

const Field = forwardRef(
  /**
   * @param {{
   *   required?: true | false
   * } & ComponentPropsWithoutRef<typeof FormPrimitive.Field>} props
   */
  function Field(
    { name, hidden, required, children, serverInvalid, ...props },
    ref,
  ) {
    const { formMessage } = useFormState()
    const problems = formMessage?.fields[name] || serverInvalid

    const [validity, setValidity] = useState(
      /** @type {ValidityState} */ (undefined),
    )

    const isValid = !problems && (validity ? validity.valid : undefined)
    const isInvalid = !!problems || (validity ? !validity.valid : undefined)
    const isRequired = !!required

    const fieldState = {
      'data-valid': isValid,
      'data-invalid': isInvalid,
      'data-required': isRequired,
    }

    const fieldRef = useRef()
    useImperativeHandle(ref, () => {
      return fieldRef.current
    }, [])

    return (
      <FieldPropsContext.Provider value={{ hidden, required }}>
        <FieldValidityContext.Provider value={{ validity }}>
          <FieldStateContext.Provider value={fieldState}>
            <FormPrimitive.Field
              {...fieldState}
              hidden={hidden}
              name={name}
              onInvalid={(event) => {
                props.onInvalid?.(event)
                if (event.defaultPrevented) {
                  return
                }

                const dispatcher = /** @type {HTMLInputElement} */ (
                  event.target
                )
                const target = /** @type {HTMLElement} */ (fieldRef.current)
                if (dispatcher.closest(`.${styles.field}`) === target) {
                  setValidity(dispatcher.validity)
                }
              }}
              ref={fieldRef}
              serverInvalid={!!problems}
              {...mergeProps(props, { className: styles.field })}
            >
              {children}
              <ProblemDetails as={ValidationMessage}>{problems}</ProblemDetails>
              <ValidityState onValidityChanged={setValidity} />
            </FormPrimitive.Field>
          </FieldStateContext.Provider>
        </FieldValidityContext.Provider>
      </FieldPropsContext.Provider>
    )
  },
)

const Label = forwardRef(
  /** @param {ComponentPropsWithoutRef<typeof FormPrimitive.Label>} props */
  function Label(props, ref) {
    const state = useFieldState()

    return (
      <FormPrimitive.Label
        {...mergeProps(props, state, {
          className: styles.label,
        })}
        ref={ref}
      />
    )
  },
)

const Control = forwardRef(
  /**
   * @param {{
   *   variant?: 'normal' | 'inline' | 'inline-start' | 'inline-end' | 'hidden'
   * } & Omit<
   *   ComponentPropsWithoutRef<typeof FormPrimitive.Control>,
   *   'hidden' | 'required'
   * >} props
   */
  function Control({ children, ...props }, ref) {
    const { hidden, required } = useFieldProps()
    const state = useFieldState()

    return (
      <FormPrimitive.Control
        asChild={!!children}
        hidden={hidden}
        ref={ref}
        required={required}
        {...mergeProps(
          {
            variant: 'soft',
            className: styles.control,
          },
          props,
          state,
        )}
      >
        {children}
      </FormPrimitive.Control>
    )
  },
)

const ValidationMessage = forwardRef(
  /** @param {FormMessageProps} props */
  function ValidationMessage(props, ref) {
    return (
      <FormPrimitive.Message
        ref={ref}
        {...mergeProps(props, { className: styles.message })}
      />
    )
  },
)

const Description = forwardRef(
  /** @param {ComponentPropsWithoutRef<typeof Text>} props */
  function Description({ children, ...props }, ref) {
    return (
      <Text
        as={typeof children === 'string' ? 'p' : 'div'}
        ref={ref}
        size="1"
        {...mergeProps(props, {
          className: styles.description,
        })}
      >
        {children}
      </Text>
    )
  },
)

const Submit = forwardRef(
  /**
   * @param {{
   *   disabledOnSubmit?: boolean
   * } & ComponentPropsWithoutRef<typeof FormPrimitive.Submit>} props
   */
  function Submit(
    { disabledOnSubmit = true, disabled, asChild, children, ...props },
    ref,
  ) {
    const { isSubmitting } = useFormState()

    const SubmitButton = asChild ? Slot : Button

    return (
      <FormPrimitive.Submit
        asChild
        disabled={disabledOnSubmit ? isSubmitting : disabled}
        ref={ref}
        type="submit"
        {...mergeProps(props, {
          loading: isSubmitting,
          className: styles.submit,
        })}
      >
        <SubmitButton>{children}</SubmitButton>
      </FormPrimitive.Submit>
    )
  },
)

/**
 * @param {Partial<
 *   {
 *     onValidityChanged: (validity: ValidityState) => void
 *   } & ComponentProps<FormPrimitive.ValidityState>
 * >} props
 */
function ValidityState({ children, onValidityChanged, ...props }) {
  const requestAnimationFrame =
    typeof window === 'undefined' ? undefined : window.requestAnimationFrame

  return (
    <FormPrimitive.ValidityState {...props}>
      {(validity) => {
        if (onValidityChanged && requestAnimationFrame) {
          requestAnimationFrame(() => {
            onValidityChanged(validity)
          })
        }

        return children?.(validity) ?? null
      }}
    </FormPrimitive.ValidityState>
  )
}

/** Utilities */
function ProblemDetails({ as: MessageElement, children: problems }) {
  if (!problems || !Array.isArray(problems) || problems.length === 0) {
    return null
  }

  return (
    <Flex className={styles.message} gap="2">
      {problems.map((problem) => {
        const message = [problem.detail ?? problem.message, problem.remediation]
          .filter(Boolean)
          .map((p) => `${p.replace(/\.$/, '')}.`)
          .join(' ')

        return <MessageElement key={message}>{message}</MessageElement>
      })}
    </Flex>
  )
}
